import React, { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Clock, Play, Square, Coffee, Calendar, FileText, Users, PalmtreeIcon,
  CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight, Plus,
  Send, RotateCcw, Trash2, Edit, CalendarDays
} from "lucide-react";

// Cost codes for construction
const COST_CODES = [
  { code: "LAB-GEN", label: "General Labor" },
  { code: "LAB-FRM", label: "Framing" },
  { code: "LAB-DRY", label: "Drywall" },
  { code: "LAB-HVC", label: "HVAC" },
  { code: "LAB-ELE", label: "Electrical" },
  { code: "LAB-PLB", label: "Plumbing" },
  { code: "LAB-OTH", label: "Other" },
];

// Helper functions
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const formatHours = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const getWeekDates = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const current = new Date(monday);
    current.setDate(monday.getDate() + i);
    dates.push(current.toISOString().split("T")[0]);
  }
  return dates;
};

// Clock Widget Component
const ClockWidget = ({ projects, onClockChange }) => {
  const [clockStatus, setClockStatus] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedCostCode, setSelectedCostCode] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [enableGpsTracking, setEnableGpsTracking] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClockStatus = useCallback(async () => {
    try {
      const response = await API.get("/clock/status");
      setClockStatus(response.data);
      if (response.data.clocked_in && response.data.active_clock) {
        const clockIn = new Date(response.data.active_clock.clock_in);
        const now = new Date();
        setTimerSeconds(Math.floor((now - clockIn) / 1000));
      }
    } catch (error) {
      console.error("Error fetching clock status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch available vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const response = await API.get("/mileage/vehicles");
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  }, []);

  useEffect(() => {
    fetchClockStatus();
    fetchVehicles();
  }, [fetchClockStatus, fetchVehicles]);

  useEffect(() => {
    let interval;
    if (clockStatus?.clocked_in) {
      interval = setInterval(() => {
        setTimerSeconds((s) => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [clockStatus?.clocked_in]);

  const handleClockIn = async () => {
    if (!selectedProject) {
      toast.error("Please select a project");
      return;
    }
    try {
      // Get GPS location if tracking enabled
      let location = null;
      if (enableGpsTracking && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000
            });
          });
          location = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
        } catch (geoErr) {
          console.warn("Could not get location:", geoErr);
        }
      }

      await API.post("/clock/in", {
        project_id: selectedProject,
        cost_code: selectedCostCode || null,
        vehicle_id: selectedVehicle && selectedVehicle !== "none" ? selectedVehicle : null,
        gps_tracking_enabled: enableGpsTracking,
        location: location
      });
      toast.success("Clocked in!");
      fetchClockStatus();
      onClockChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    }
  };

  const handleClockOut = async () => {
    try {
      await API.post("/clock/out", {});
      toast.success("Clocked out!");
      setTimerSeconds(0);
      fetchClockStatus();
      onClockChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    }
  };

  const handleStartBreak = async (breakType) => {
    try {
      await API.post("/clock/break/start", { break_type: breakType });
      toast.success(`${breakType === "lunch" ? "Lunch" : "Paid"} break started`);
      fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start break");
    }
  };

  const handleEndBreak = async () => {
    try {
      await API.post("/clock/break/end", {});
      toast.success("Break ended");
      fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to end break");
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId);
    return project?.name || "Unknown";
  };

  if (loading) {
    return <div className="animate-pulse bg-slate-800 border border-slate-700 rounded-lg p-6 h-40" />;
  }

  const isOnBreak = clockStatus?.active_clock?.current_break;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <h3 className="font-chivo font-semibold text-lg text-white mb-4 flex items-center gap-2">
        <Clock size={20} className="text-emerald-500" />
        Time Clock
      </h3>

      {clockStatus?.clocked_in ? (
        <div className="space-y-4">
          {/* Timer Display */}
          <div className="text-center">
            <div className={`text-5xl font-chivo font-bold tabular-nums ${isOnBreak ? "text-orange-400" : "text-emerald-500"}`}>
              {formatTime(timerSeconds)}
            </div>
            <p className="text-slate-400 mt-2">
              Working on: <span className="font-medium text-slate-200">{getProjectName(clockStatus.active_clock.project_id)}</span>
            </p>
            {clockStatus.active_clock.cost_code && (
              <p className="text-sm text-slate-500">
                Cost Code: {COST_CODES.find(c => c.code === clockStatus.active_clock.cost_code)?.label || clockStatus.active_clock.cost_code}
              </p>
            )}
            {clockStatus.active_clock.vehicle_id && (
              <p className="text-sm text-emerald-400">
                Vehicle: {vehicles.find(v => v.vehicle_id === clockStatus.active_clock.vehicle_id)?.make || ''} {vehicles.find(v => v.vehicle_id === clockStatus.active_clock.vehicle_id)?.model || 'Assigned'}
              </p>
            )}
            {clockStatus.gps_tracking_enabled && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm font-medium">
                GPS Tracking Active
              </div>
            )}
            {isOnBreak && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm font-medium">
                <Coffee size={14} />
                On {isOnBreak.break_type === "lunch" ? "Lunch" : "Paid"} Break
              </div>
            )}
          </div>

          {/* Break History */}
          {clockStatus.active_clock.breaks?.length > 0 && (
            <div className="text-sm text-slate-500 text-center">
              Breaks taken: {clockStatus.active_clock.breaks.length} ({formatHours(clockStatus.active_clock.breaks.reduce((sum, b) => sum + b.duration, 0))})
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-center">
            {!isOnBreak ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleStartBreak("lunch")}
                  className="flex-1 min-w-[120px]"
                  data-testid="lunch-break-btn"
                >
                  <Coffee size={16} className="mr-2" />
                  Lunch Break
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleStartBreak("paid")}
                  className="flex-1 min-w-[120px]"
                  data-testid="paid-break-btn"
                >
                  <Coffee size={16} className="mr-2" />
                  Paid Break
                </Button>
              </>
            ) : (
              <Button
                onClick={handleEndBreak}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                data-testid="end-break-btn"
              >
                <Play size={16} className="mr-2" />
                End Break
              </Button>
            )}
          </div>

          <Button
            onClick={handleClockOut}
            className="w-full bg-rose-500 hover:bg-rose-600 text-white"
            data-testid="clock-out-btn"
          >
            <Square size={16} className="mr-2" />
            Clock Out
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-300 mb-1 block">Project *</label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="clock-project-select" className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {projects.filter(p => p.status === "active").map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id} className="text-slate-200 focus:bg-slate-700">
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300 mb-1 block">Cost Code</label>
            <Select value={selectedCostCode} onValueChange={setSelectedCostCode}>
              <SelectTrigger data-testid="clock-cost-code-select" className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Select cost code (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {COST_CODES.map((cc) => (
                  <SelectItem key={cc.code} value={cc.code} className="text-slate-200 focus:bg-slate-700">
                    {cc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vehicle Selection */}
          <div>
            <label className="text-sm font-medium text-slate-300 mb-1 block">Vehicle</label>
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger data-testid="clock-vehicle-select" className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Select vehicle (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="none" className="text-slate-200 focus:bg-slate-700">
                  No vehicle
                </SelectItem>
                {vehicles.filter(v => v.status === "active").map((vehicle) => (
                  <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id} className="text-slate-200 focus:bg-slate-700">
                    {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.license_plate ? `(${vehicle.license_plate})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* GPS Tracking Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700">
            <div>
              <div className="text-sm font-medium text-slate-300">GPS Mileage Tracking</div>
              <div className="text-xs text-slate-500">Auto-track trips while on the clock</div>
            </div>
            <button
              onClick={() => setEnableGpsTracking(!enableGpsTracking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enableGpsTracking ? 'bg-emerald-600' : 'bg-slate-600'
              }`}
              data-testid="gps-tracking-toggle"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enableGpsTracking ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <Button
            onClick={handleClockIn}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-14 text-lg"
            data-testid="clock-in-btn"
          >
            <Play size={20} className="mr-2" />
            Clock In
          </Button>
        </div>
      )}
    </div>
  );
};

// Timesheet Component
const TimesheetView = ({ user }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [timesheetToReject, setTimesheetToReject] = useState(null);

  const canManage = ["owner", "office_manager", "project_manager"].includes(user?.role);

  const currentWeek = getWeekDates(new Date(Date.now() + weekOffset * 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]);
  const weekStart = currentWeek[0];
  const weekEnd = currentWeek[6];

  const fetchTimesheets = useCallback(async () => {
    try {
      const response = await API.get("/timesheets");
      setTimesheets(response.data);
    } catch (error) {
      console.error("Error fetching timesheets:", error);
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      const response = await API.get("/time-entries", {
        params: { week_start: weekStart }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching entries:", error);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchTimesheets();
    fetchEntries();
  }, [fetchTimesheets, fetchEntries]);

  const generateTimesheet = async () => {
    try {
      await API.post("/timesheets/generate", { week_start: weekStart });
      toast.success("Timesheet generated");
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate timesheet");
    }
  };

  const submitTimesheet = async (timesheetId) => {
    try {
      await API.post(`/timesheets/${timesheetId}/submit`);
      toast.success("Timesheet submitted for approval");
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit timesheet");
    }
  };

  const approveTimesheet = async (timesheetId) => {
    try {
      await API.post(`/timesheets/${timesheetId}/approve`);
      toast.success("Timesheet approved");
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve timesheet");
    }
  };

  const rejectTimesheet = async () => {
    if (!timesheetToReject) return;
    try {
      await API.post(`/timesheets/${timesheetToReject}/reject`, { reason: rejectReason });
      toast.success("Timesheet rejected");
      setRejectDialogOpen(false);
      setRejectReason("");
      setTimesheetToReject(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject timesheet");
    }
  };

  const reopenTimesheet = async (timesheetId) => {
    try {
      await API.post(`/timesheets/${timesheetId}/reopen`);
      toast.success("Timesheet reopened");
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reopen timesheet");
    }
  };

  const currentTimesheet = timesheets.find(t => t.week_start === weekStart);
  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const regularHours = Math.min(totalHours, 40);
  const overtimeHours = Math.max(0, totalHours - 40);

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: "bg-slate-800", text: "text-slate-600", icon: Edit },
      submitted: { bg: "bg-blue-100", text: "text-blue-700", icon: Send },
      approved: { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle },
      rejected: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
      reopened: { bg: "bg-amber-100", text: "text-amber-700", icon: RotateCcw },
    };
    const style = styles[status] || styles.draft;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Group entries by date
  const entriesByDate = {};
  currentWeek.forEach(date => { entriesByDate[date] = []; });
  entries.forEach(entry => {
    if (entriesByDate[entry.date]) {
      entriesByDate[entry.date].push(entry);
    }
  });

  if (loading) {
    return <div className="animate-pulse">Loading timesheet...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setWeekOffset(o => o - 1)}>
          <ChevronLeft size={16} className="mr-1" /> Previous Week
        </Button>
        <div className="text-center">
          <h3 className="font-chivo font-semibold text-lg text-slate-200">
            Week of {new Date(weekStart).toLocaleDateString("en-US", { month: "short", day: "numeric" })} - {new Date(weekEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </h3>
          {currentTimesheet && getStatusBadge(currentTimesheet.status)}
        </div>
        <Button variant="outline" onClick={() => setWeekOffset(o => o + 1)}>
          Next Week <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-500">Total Hours</p>
          <p className="text-2xl font-chivo font-bold text-slate-200">{totalHours.toFixed(2)}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-500">Regular Hours</p>
          <p className="text-2xl font-chivo font-bold text-slate-200">{regularHours.toFixed(2)}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-500">Overtime Hours</p>
          <p className={`text-2xl font-chivo font-bold ${overtimeHours > 0 ? "text-orange-400" : "text-slate-200"}`}>
            {overtimeHours.toFixed(2)}
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-500">Entries</p>
          <p className="text-2xl font-chivo font-bold text-slate-200">{entries.length}</p>
        </div>
      </div>

      {/* Daily Breakdown */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-slate-800">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => (
            <div key={day} className="p-3 text-center border-r last:border-r-0">
              <p className="text-xs font-semibold text-slate-500">{day}</p>
              <p className="text-sm text-slate-300">{new Date(currentWeek[i]).getDate()}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {currentWeek.map((date) => {
            const dayEntries = entriesByDate[date] || [];
            const dayTotal = dayEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
            return (
              <div key={date} className="p-3 border-r last:border-r-0 min-h-[100px]">
                {dayEntries.length > 0 ? (
                  <div className="space-y-1">
                    {dayEntries.map((entry) => (
                      <div key={entry.entry_id} className="text-xs bg-slate-800 rounded p-1">
                        <p className="font-medium truncate">{entry.hours?.toFixed(2)}h</p>
                      </div>
                    ))}
                    <p className="text-xs font-semibold text-slate-600 pt-1 border-t">
                      Total: {dayTotal.toFixed(2)}h
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center">No entries</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Timesheet Actions */}
      <div className="flex flex-wrap gap-3">
        {!currentTimesheet && entries.length > 0 && (
          <Button onClick={generateTimesheet} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            <FileText size={16} className="mr-2" />
            Generate Timesheet
          </Button>
        )}
        {currentTimesheet?.status === "draft" && (
          <Button onClick={() => submitTimesheet(currentTimesheet.timesheet_id)} className="bg-blue-600 hover:bg-blue-700">
            <Send size={16} className="mr-2" />
            Submit for Approval
          </Button>
        )}
        {currentTimesheet?.status === "rejected" && (
          <>
            <Button onClick={() => submitTimesheet(currentTimesheet.timesheet_id)} className="bg-blue-600 hover:bg-blue-700">
              <Send size={16} className="mr-2" />
              Resubmit
            </Button>
            <p className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle size={14} />
              Rejection reason: {currentTimesheet.rejection_reason || "Not specified"}
            </p>
          </>
        )}
        {canManage && currentTimesheet?.status === "submitted" && (
          <>
            <Button onClick={() => approveTimesheet(currentTimesheet.timesheet_id)} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle size={16} className="mr-2" />
              Approve
            </Button>
            <Button
              variant="outline"
              className="text-red-600 border-red-500/50 hover:bg-red-500/10"
              onClick={() => {
                setTimesheetToReject(currentTimesheet.timesheet_id);
                setRejectDialogOpen(true);
              }}
            >
              <XCircle size={16} className="mr-2" />
              Reject
            </Button>
          </>
        )}
        {canManage && currentTimesheet?.status === "approved" && (
          <Button variant="outline" onClick={() => reopenTimesheet(currentTimesheet.timesheet_id)}>
            <RotateCcw size={16} className="mr-2" />
            Reopen Timesheet
          </Button>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300">Reason for Rejection</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20"
                rows={3}
                placeholder="Please provide a reason..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button onClick={rejectTimesheet} className="bg-red-600 hover:bg-red-700">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Time Off Component
const TimeOffView = ({ user }) => {
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [form, setForm] = useState({
    policy_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [policyForm, setPolicyForm] = useState({
    name: "",
    policy_type: "paid",
    max_balance: "",
  });

  const canManage = ["owner", "office_manager", "project_manager"].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const [policiesRes, requestsRes] = await Promise.all([
        API.get("/time-off/policies"),
        API.get("/time-off/requests"),
      ]);
      setPolicies(policiesRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error("Error fetching time off data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      await API.post("/time-off/requests", form);
      toast.success("Time off request submitted");
      setDialogOpen(false);
      setForm({ policy_id: "", start_date: "", end_date: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    try {
      await API.post("/time-off/policies", {
        ...policyForm,
        max_balance: parseFloat(policyForm.max_balance) || 0,
      });
      toast.success("Policy created");
      setPolicyDialogOpen(false);
      setPolicyForm({ name: "", policy_type: "paid", max_balance: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create policy");
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await API.post(`/time-off/requests/${requestId}/approve`);
      toast.success("Request approved");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleDeny = async (requestId) => {
    try {
      await API.post(`/time-off/requests/${requestId}/deny`, { reason: "Denied by manager" });
      toast.success("Request denied");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny");
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm("Cancel this time off request?")) return;
    try {
      await API.delete(`/time-off/requests/${requestId}`);
      toast.success("Request cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
      approved: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
      denied: "bg-red-500/20 text-red-400 border border-red-500/30",
    };
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div className="animate-pulse">Loading time off...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              <Plus size={16} className="mr-2" />
              Request Time Off
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Time Off</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300">Type *</label>
                <Select value={form.policy_id} onValueChange={(v) => setForm({ ...form, policy_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.map((p) => (
                      <SelectItem key={p.policy_id} value={p.policy_id}>
                        {p.name} ({p.policy_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300">Start Date *</label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300">End Date *</label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Reason</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-700 rounded-md text-sm"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Submit Request</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {canManage && (
          <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus size={16} className="mr-2" />
                Add Policy
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Time Off Policy</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePolicy} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300">Policy Name *</label>
                  <Input
                    value={policyForm.name}
                    onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                    required
                    placeholder="e.g., Vacation, Sick Leave"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300">Type</label>
                  <Select value={policyForm.policy_type} onValueChange={(v) => setPolicyForm({ ...policyForm, policy_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300">Max Balance (hours)</label>
                  <Input
                    type="number"
                    value={policyForm.max_balance}
                    onChange={(e) => setPolicyForm({ ...policyForm, max_balance: e.target.value })}
                    placeholder="e.g., 80"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setPolicyDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Create Policy</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Policies */}
      {policies.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <h4 className="font-semibold text-slate-200 mb-3">Time Off Policies</h4>
          <div className="flex flex-wrap gap-2">
            {policies.map((p) => (
              <div key={p.policy_id} className="px-3 py-2 bg-slate-800 rounded-md text-sm">
                <span className="font-medium">{p.name}</span>
                <span className="text-slate-500 ml-2">({p.policy_type}, {p.max_balance}h max)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests */}
      {requests.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center">
          <PalmtreeIcon size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No time off requests</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Dates</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Hours</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {requests.map((req) => (
                <tr key={req.request_id}>
                  <td className="px-4 py-3 font-medium text-slate-200">{req.user_name}</td>
                  <td className="px-4 py-3 text-slate-600">{req.policy_name}</td>
                  <td className="px-4 py-3 text-slate-600">{req.start_date} → {req.end_date}</td>
                  <td className="px-4 py-3 text-slate-600">{req.hours}h</td>
                  <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {canManage && req.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApprove(req.request_id)}>
                            <CheckCircle size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeny(req.request_id)}>
                            <XCircle size={14} />
                          </Button>
                        </>
                      )}
                      {req.status === "pending" && req.user_id === user?.user_id && (
                        <Button size="sm" variant="outline" className="text-slate-600" onClick={() => handleCancel(req.request_id)}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Holidays Component
const HolidaysView = ({ user }) => {
  const [holidays, setHolidays] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", paid: true, hours: "8" });
  const [loading, setLoading] = useState(true);

  const canManage = ["owner", "office_manager"].includes(user?.role);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await API.get("/holidays", { params: { year: new Date().getFullYear() } });
      setHolidays(response.data);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post("/holidays", {
        ...form,
        hours: parseFloat(form.hours) || 8,
      });
      toast.success("Holiday added");
      setDialogOpen(false);
      setForm({ name: "", date: "", paid: true, hours: "8" });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add holiday");
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await API.delete(`/holidays/${holidayId}`);
      toast.success("Holiday deleted");
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading holidays...</div>;
  }

  return (
    <div className="space-y-6">
      {canManage && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              <Plus size={16} className="mr-2" />
              Add Holiday
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Company Holiday</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300">Holiday Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g., Christmas Day"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Date *</label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300">Paid Hours</label>
                  <Input
                    type="number"
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: e.target.value })}
                    placeholder="8"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="paid"
                    checked={form.paid}
                    onChange={(e) => setForm({ ...form, paid: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="paid" className="text-sm text-slate-300">Paid Holiday</label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Holiday</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {holidays.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center">
          <CalendarDays size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No holidays scheduled for {new Date().getFullYear()}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {holidays.map((h) => (
            <div key={h.holiday_id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-slate-200">{h.name}</h4>
                  <p className="text-sm text-slate-500">{new Date(h.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {h.paid ? `${h.hours}h paid` : "Unpaid"}
                  </p>
                </div>
                {canManage && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(h.holiday_id)} className="text-red-500">
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Time Tracking Page
const TimeTrackingPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState("clock");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await API.get("/projects");
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  const handleClockChange = () => {
    setRefreshKey(k => k + 1);
  };

  return (
    <div data-testid="time-tracking-page" className="space-y-6 animate-slide-in">
      <div>
        <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Time Management</h1>
        <p className="text-slate-500 font-ibm mt-1">Clock in/out, manage timesheets, and request time off</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="clock" className="flex items-center gap-2">
            <Clock size={16} />
            <span className="hidden sm:inline">Clock</span>
          </TabsTrigger>
          <TabsTrigger value="timesheets" className="flex items-center gap-2">
            <FileText size={16} />
            <span className="hidden sm:inline">Timesheets</span>
          </TabsTrigger>
          <TabsTrigger value="timeoff" className="flex items-center gap-2">
            <PalmtreeIcon size={16} />
            <span className="hidden sm:inline">Time Off</span>
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <CalendarDays size={16} />
            <span className="hidden sm:inline">Holidays</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clock" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ClockWidget projects={projects} onClockChange={handleClockChange} />
            <div className="lg:col-span-2">
              <TimesheetView key={refreshKey} user={user} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timesheets" className="mt-6">
          <TimesheetView key={refreshKey} user={user} />
        </TabsContent>

        <TabsContent value="timeoff" className="mt-6">
          <TimeOffView user={user} />
        </TabsContent>

        <TabsContent value="holidays" className="mt-6">
          <HolidaysView user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TimeTrackingPage;
